import React from "react";
import { css, useTheme } from "@emotion/react";

import ContentCombine from "core/Components/ContentCombine";
import DatePeriod from "core/Components/Item/DatePeriod";
import { getTwelveHourTime } from "core/includes/dates";

type Props = {
    title: string,
    startDate: string,
    endDate: string,
    startTime?: string,
    endTime?: string,
    location: string,
    className?: string,
};

const EventDetail: React.FC<Props> = ({
    title,
    startDate,
    endDate,
    startTime,
    endTime,
    location,
    className = '',
}) => {
    const theme = useTheme();

    const leftContainerStyle = css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        ${theme.breakpoints.up('3xl')} {
            padding-top: 68px;
        }
        
        ${theme.breakpoints.between('xl', 'xxl')} {
            padding-top: 64px;
        }

        ${theme.breakpoints.only('lg')} {
            padding-top: 75px;
        }
    `;

    const detailContainerStyle = css`
        width: 362px;
        padding: 18px 16px 28px;
        display: flex;
        flex-direction: column;
        background-color: ${theme.colours.pattensBlue4};
        border-radius: 17px;

        ${theme.breakpoints.only('lg')} {
            width: 315px;
            padding-bottom: 24px;
        }
        
        ${theme.breakpoints.between('sm', 'md')} {
            width: 100%;
            display: grid;
        }
        
        ${theme.breakpoints.only('md')} {
            grid-template-columns: 3fr 2fr;
        }

        ${theme.breakpoints.only('sm')} {
            grid-template-columns: 1fr 1fr;
        }
        
        ${theme.breakpoints.only('md')} {
            padding: 14px 16px 26px 32px;
        }

        ${theme.breakpoints.only('sm')} {
            padding: 0 14px 16px;
        }

        ${theme.breakpoints.only('xs')} {
            padding: 0 16px 12px;
        }
    `;

    const prefixStyle = css`
        font-weight: ${theme.fonts.weights.bold};
        padding-bottom: 8px;
    `;

    const titleStyle = css`
        ${prefixStyle};
        
        ${theme.breakpoints.down('md')} {
            display: none;
        }
    `;

    const detailKeyStyle = css`
        width: 100px;
        justify-content: flex-start;
        color: ${theme.colours.curiousBlue2};
        flex-shrink: 0;
        
        ${theme.breakpoints.down('md')} {
            width: 90px;
        }
    `;

    const eventDetailContainerStyle = css`
        display: flex;
        margin-top: 11px;
        padding-right: 18px;
    `;

    const detailValueStyle = css`
        font-size: 14px;
        line-height: 17px;
        display: flex;
        align-items: center;
    `;

    return (
        <div css={leftContainerStyle} className={className}>
            <ContentCombine
                fontSizeType={'20'}
                css={prefixStyle}
            >
                Event details
            </ContentCombine>
            <div css={detailContainerStyle} className={'detail-container'}>
                <ContentCombine
                    css={titleStyle}
                    fontSizeType={'20'}
                >
                    {title}
                </ContentCombine>
                <div css={eventDetailContainerStyle}>
                    <ContentCombine
                        css={detailKeyStyle}
                        fontSizeType={'22'}
                    >
                        Date:
                    </ContentCombine>
                    <div css={detailValueStyle}>
                        <DatePeriod
                            start={startDate}
                            end={endDate}
                            isAbbreviationMonths={false}
                        />
                    </div>
                </div>
                <div css={eventDetailContainerStyle}>
                    <ContentCombine
                        css={detailKeyStyle}
                        fontSizeType={'22'}
                    >
                        Time:
                    </ContentCombine>
                    {(startTime && endTime) && (
                        <div css={detailValueStyle}>
                            {getTwelveHourTime(startTime, true)} - {getTwelveHourTime(endTime, true)}
                        </div>
                    )}
                </div>
                <div css={eventDetailContainerStyle}>
                    <ContentCombine
                        css={detailKeyStyle}
                        fontSizeType={'22'}
                    >
                        Location:
                    </ContentCombine>
                    <div css={detailValueStyle}>
                        {location}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EventDetail;
